import React, { useCallback, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import QRCode from "qrcode.react";
import { makeStyles } from "@material-ui/core/styles";
import {
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	IconButton,
	LinearProgress,
	MenuItem,
	Select,
	TextField,
	Tooltip
} from "@material-ui/core";
import {
	Close,
	Contacts as ContatosIcon,
	VpnKey,
	AccountCircle,
	SettingsInputComponent,
	History,
	ErrorOutline,
	FileCopyOutlined,
	CheckCircle
} from "@material-ui/icons";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ConfirmarAcaoModal from "../ConfirmarAcaoModal";
import {
	modalPapel,
	modalHero,
	modalHeroLinha,
	modalHeroIcone,
	modalHeroEtiqueta,
	modalHeroTitulo,
	modalHeroSub,
	modalHeroChip,
	modalFechar,
	modalCorpo,
	modalSecao,
	modalSecaoTopo,
	modalSecaoIcone,
	modalSecaoTitulo,
	modalSecaoSub,
	campoNavy,
	botaoPrimario,
	botaoSecundario,
	botaoPerigo
} from "../../utils/estiloNavy";

// Agenda do celular pelo Google Contacts (backend:
// services/GoogleContatosServices). Credenciais do Google Cloud, contas Google
// conectadas, qual conta cada conexao usa e a fila (um contato por vez).

const useStyles = makeStyles(theme => {
	const dark = theme.palette.type === "dark";
	return {
		paper: modalPapel(theme, 860),
		hero: modalHero(theme),
		heroLinha: { ...modalHeroLinha, flexWrap: "wrap" },
		heroIcone: modalHeroIcone,
		etiqueta: modalHeroEtiqueta,
		titulo: modalHeroTitulo,
		sub: modalHeroSub,
		chip: { ...modalHeroChip, marginLeft: "auto" },
		fechar: modalFechar,
		corpo: modalCorpo(theme),
		secao: modalSecao(theme),
		secaoTopo: modalSecaoTopo,
		secaoIcone: modalSecaoIcone,
		secaoTitulo: modalSecaoTitulo(theme),
		secaoSub: modalSecaoSub(theme),
		campo: { ...campoNavy(theme), marginBottom: 12 },
		primario: botaoPrimario,
		secundario: botaoSecundario(theme),
		perigo: botaoPerigo,
		texto: { fontSize: "0.84rem", color: theme.palette.text.secondary, lineHeight: 1.55 },
		codigo: {
			display: "flex",
			alignItems: "center",
			gap: 8,
			fontFamily: "monospace",
			fontSize: "0.8rem",
			padding: "8px 10px",
			borderRadius: 10,
			marginTop: 6,
			wordBreak: "break-all",
			backgroundColor: dark ? "rgba(255,255,255,0.05)" : "#eef2f6",
			color: theme.palette.text.primary
		},
		ok: { display: "flex", alignItems: "center", gap: 8, fontSize: "0.86rem", color: theme.palette.text.primary, flexWrap: "wrap" },
		conta: {
			borderRadius: 14,
			padding: "12px 14px",
			marginTop: 10,
			border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"}`,
			backgroundColor: dark ? "rgba(255,255,255,0.03)" : "#fff"
		},
		contaTopo: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
		email: { fontWeight: 800, fontSize: "0.9rem", flex: 1, minWidth: 200, color: theme.palette.text.primary },
		pill: { fontSize: "0.7rem", fontWeight: 800, padding: "3px 9px", borderRadius: 20, whiteSpace: "nowrap" },
		pillVerde: { color: "#15803d", backgroundColor: "rgba(34,197,94,.15)" },
		pillVermelha: { color: "#b91c1c", backgroundColor: "rgba(220,38,38,.12)" },
		numeros: { display: "flex", gap: 16, flexWrap: "wrap", marginTop: 6, fontSize: "0.8rem", color: theme.palette.text.secondary, "& b": { color: theme.palette.text.primary } },
		erroConta: { fontSize: "0.76rem", color: "#dc2626", marginTop: 4 },
		barra: { marginTop: 8, height: 6, borderRadius: 3 },
		conexao: {
			display: "flex",
			alignItems: "center",
			gap: 8,
			flexWrap: "wrap",
			padding: "8px 0",
			borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)"}`,
			"&:last-child": { borderBottom: "none" }
		},
		nomeConexao: { flex: 1, minWidth: 170, fontSize: "0.86rem", fontWeight: 700, color: theme.palette.text.primary },
		select: { minWidth: 240, fontSize: "0.84rem" },
		linha: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
		erroLinha: { fontSize: "0.76rem", color: theme.palette.text.secondary, padding: "3px 0" },
		carregando: { display: "flex", justifyContent: "center", padding: 40 }
	};
});

const Secao = ({ classes, icone, titulo, sub, children }) => (
	<div className={classes.secao}>
		<div className={classes.secaoTopo}>
			<div className={classes.secaoIcone}>{icone}</div>
			<div>
				<div className={classes.secaoTitulo}>{titulo}</div>
				{sub && <div className={classes.secaoSub}>{sub}</div>}
			</div>
		</div>
		{children}
	</div>
);

const GoogleContatosModal = ({ open, onClose }) => {
	const classes = useStyles();
	const { user } = useContext(AuthContext);
	const ehAdmin = user?.profile === "admin";

	const [dados, setDados] = useState(null);
	const [editandoCred, setEditandoCred] = useState(false);
	const [clientId, setClientId] = useState("");
	const [clientSecret, setClientSecret] = useState("");
	const [salvando, setSalvando] = useState(false);
	const [dias, setDias] = useState(30);
	const [colarAberto, setColarAberto] = useState(false);
	const [link, setLink] = useState("");
	// QR Code para conectar a conta Google PELO CELULAR
	const [qr, setQr] = useState(null);
	// Conta Google a desconectar (confirmacao)
	const [desconectando, setDesconectando] = useState(null);

	const carregar = useCallback(async () => {
		try {
			const { data } = await api.get("/google-contatos");
			setDados(data);
		} catch (err) {
			toastError(err);
		}
	}, []);

	useEffect(() => {
		if (!open) return undefined;
		carregar();
		// Com o QR na tela, confere rapido se o celular ja conectou.
		const t = setInterval(carregar, qr ? 3000 : 10000);
		return () => clearInterval(t);
	}, [open, carregar, qr]);

	// O celular conectou: a conta aparece (ou volta a ficar ativa) na lista.
	useEffect(() => {
		if (!qr || !dados) return;
		const ativasAgora = (dados.contas || []).filter(c => c.ativa).map(c => `${c.id}:${c.email}`);
		const nova = ativasAgora.find(c => !qr.antes.includes(c));
		if (nova) {
			toast.success(`Conta Google conectada: ${nova.split(":").slice(1).join(":")}`);
			setQr(null);
		}
	}, [dados, qr]);

	const conectarPeloCelular = async (whatsappId, padrao, alvo) => {
		try {
			const { data } = await api.post("/google-contatos/autorizar", { whatsappId, padrao, celular: true });
			const antes = (dados?.contas || []).filter(c => c.ativa).map(c => `${c.id}:${c.email}`);
			setQr({ url: data.url, alvo, antes });
		} catch (err) {
			toastError(err);
		}
	};

	// Volta do Google (?google=ok / ?google=erro)
	useEffect(() => {
		const q = new URLSearchParams(window.location.search);
		const r = q.get("google");
		if (!r) return;
		if (r === "ok") toast.success(`Conta Google conectada: ${q.get("email") || ""}`);
		else toast.error(q.get("msg") || "Não foi possível conectar a conta Google.");
		window.history.replaceState(null, "", window.location.pathname);
	}, []);

	const copiar = texto => {
		try {
			navigator.clipboard.writeText(texto);
			toast.success("Copiado!");
		} catch {
			/* sem area de transferencia */
		}
	};

	const salvarCred = async () => {
		setSalvando(true);
		try {
			const { data } = await api.put("/google-contatos/credenciais", { clientId, clientSecret });
			setDados(data);
			setEditandoCred(false);
			setClientSecret("");
			toast.success("Credenciais do Google salvas.");
		} catch (err) {
			toastError(err);
		} finally {
			setSalvando(false);
		}
	};

	const conectar = async (whatsappId, padrao) => {
		try {
			const { data } = await api.post("/google-contatos/autorizar", { whatsappId, padrao });
			if (dados?.modoColar) {
				window.open(data.url, "_blank");
				setLink("");
				setColarAberto(true);
			} else {
				window.location.href = data.url;
			}
		} catch (err) {
			toastError(err);
		}
	};

	const concluirColando = async () => {
		setSalvando(true);
		try {
			const { data } = await api.post("/google-contatos/concluir", { link });
			toast.success(`Conta Google conectada: ${data.email}`);
			setColarAberto(false);
			carregar();
		} catch (err) {
			toastError(err);
		} finally {
			setSalvando(false);
		}
	};

	const acao = async (fn, msg) => {
		try {
			const r = await fn();
			if (msg) toast.success(typeof msg === "function" ? msg(r?.data) : msg);
			carregar();
		} catch (err) {
			toastError(err);
		}
	};

	const contas = dados?.contas || [];
	const contasAtivas = contas.filter(c => c.ativa);
	const padrao = contas.find(c => c.padrao && c.ativa);
	const pendentes = contas.reduce((s, c) => s + c.pendentes, 0);
	const mostrarFormCred = ehAdmin && (editandoCred || !dados?.configurado);

	return (
		<Dialog open={open} onClose={onClose} maxWidth={false} classes={{ paper: classes.paper }}>
			<div className={classes.hero}>
				<div className={classes.heroLinha}>
					<div className={classes.heroIcone}><ContatosIcon /></div>
					<div style={{ minWidth: 0 }}>
						<div className={classes.etiqueta}>Conexões</div>
						<div className={classes.titulo}>Google Contatos</div>
						<div className={classes.sub}>Salva os clientes na agenda do celular, um por vez, pela conta Google.</div>
					</div>
					{dados?.configurado && (
						<span className={classes.chip}>{pendentes ? `${pendentes} na fila` : "Fila vazia"}</span>
					)}
					<IconButton size="small" onClick={onClose} className={classes.fechar} style={dados?.configurado ? {} : { marginLeft: "auto" }}>
						<Close fontSize="small" />
					</IconButton>
				</div>
			</div>

			<DialogContent className={classes.corpo}>
				{!dados ? (
					<div className={classes.carregando}><CircularProgress size={26} /></div>
				) : (
					<>
						{/* 1. Credenciais */}
						<Secao
							classes={classes}
							icone={<VpnKey />}
							titulo="1. Credenciais do Google Cloud"
							sub="ID do cliente e chave secreta do projeto criado no Google Cloud."
						>
							{mostrarFormCred ? (
								<>
									<TextField
										className={classes.campo}
										variant="outlined"
										size="small"
										fullWidth
										label="ID do cliente"
										placeholder="123...apps.googleusercontent.com"
										value={clientId}
										onChange={e => setClientId(e.target.value)}
									/>
									<TextField
										className={classes.campo}
										variant="outlined"
										size="small"
										fullWidth
										type="password"
										autoComplete="new-password"
										label="Chave secreta do cliente"
										placeholder={dados.credencial?.segredo ? "Deixe em branco para manter a atual" : "GOCSPX-..."}
										value={clientSecret}
										onChange={e => setClientSecret(e.target.value)}
									/>
									<div className={classes.linha}>
										<Button className={classes.primario} disabled={!clientId || salvando} onClick={salvarCred}>
											{salvando ? "Salvando..." : "Salvar credenciais"}
										</Button>
										{dados.configurado && (
											<Button className={classes.secundario} onClick={() => setEditandoCred(false)}>
												Cancelar
											</Button>
										)}
									</div>
								</>
							) : dados.configurado ? (
								<div className={classes.ok}>
									<CheckCircle style={{ color: "#16a34a" }} fontSize="small" />
									<span>
										Configurado — ID <b>{dados.credencial?.clientId}</b> · chave <b>{dados.credencial?.segredo}</b>
										{dados.credencial?.origem === "env" && " (do .env do servidor)"}
									</span>
									{ehAdmin && (
										<Button
											size="small"
											className={classes.secundario}
											onClick={() => {
												setClientId(dados.credencial?.clientIdCompleto || "");
												setClientSecret("");
												setEditandoCred(true);
											}}
										>
											Alterar
										</Button>
									)}
								</div>
							) : (
								<div className={classes.texto}>Peça para um administrador colar as credenciais do Google Cloud aqui.</div>
							)}

							<div className={classes.texto} style={{ marginTop: 14 }}>
								No Google Cloud, em <b>Clientes → Consig Agenda → URIs de redirecionamento autorizados</b>, este endereço
								precisa estar cadastrado:
							</div>
							<div className={classes.codigo}>
								<span style={{ flex: 1 }}>{dados.redirectUri}</span>
								<Tooltip title="Copiar">
									<IconButton size="small" onClick={() => copiar(dados.redirectUri)}>
										<FileCopyOutlined fontSize="small" />
									</IconButton>
								</Tooltip>
							</div>
						</Secao>

						{/* 2. Contas */}
						{dados.configurado && (
							<Secao
								classes={classes}
								icone={<AccountCircle />}
								titulo="2. Contas Google dos celulares"
								sub="Entre com o e-mail que está logado no celular (sincronização de contatos ligada)."
							>
								<div className={classes.linha}>
									<Button
										className={classes.primario}
										onClick={() => conectarPeloCelular(null, !padrao, padrao ? "uma conta nova" : "a conta padrão")}
									>
										{padrao ? "Conectar outra conta pelo celular (QR Code)" : "Conectar pelo celular (QR Code)"}
									</Button>
									<Button className={classes.secundario} onClick={() => conectar(null, !padrao)}>
										Conectar neste computador
									</Button>
								</div>
								{contas.length === 0 && (
									<div className={classes.texto} style={{ marginTop: 10 }}>
										Nenhuma conta conectada ainda. Se aparecer “O Google não verificou este app”, clique em{" "}
										<b>Avançado → Acessar Consig Agenda</b>.
									</div>
								)}
								{contas.map(c => (
									<div key={c.id} className={classes.conta}>
										<div className={classes.contaTopo}>
											<span className={classes.email}>{c.email}</span>
											{c.padrao && c.ativa && <span className={`${classes.pill} ${classes.pillVerde}`}>Padrão</span>}
											{!c.ativa && <span className={`${classes.pill} ${classes.pillVermelha}`}>Sem acesso</span>}
											{c.ativa && !c.padrao && (
												<Button size="small" className={classes.secundario} onClick={() => acao(() => api.put(`/google-contatos/contas/${c.id}/padrao`), "Conta padrão alterada")}>
													Tornar padrão
												</Button>
											)}
											{!c.ativa && (
												<Button size="small" className={classes.secundario} onClick={() => conectarPeloCelular(null, false, c.email)}>
													Conectar de novo (QR Code)
												</Button>
											)}
											{c.erros > 0 && (
												<Button
													size="small"
													className={classes.secundario}
													onClick={() => acao(() => api.post(`/google-contatos/contas/${c.id}/tentar-de-novo`), d => `${d?.reenfileirados || 0} de volta na fila`)}
												>
													Tentar erros de novo
												</Button>
											)}
											{c.ativa && (
												<Button
													size="small"
													className={classes.perigo}
													onClick={() => setDesconectando(c)}
												>
													Desconectar
												</Button>
											)}
										</div>
										{c.erro && <div className={classes.erroConta}>{c.erro}</div>}
										<div className={classes.numeros}>
											<span><b>{c.salvos}</b> salvos</span>
											<span><b>{c.pendentes}</b> na fila</span>
											<span style={c.erros ? { color: "#dc2626" } : undefined}><b>{c.erros}</b> com erro</span>
										</div>
										<Tooltip title={`O Google aceita até ${c.limite.toLocaleString("pt-BR")} contatos por conta`}>
											<LinearProgress
												variant="determinate"
												value={Math.min(100, (c.salvos / c.limite) * 100)}
												className={classes.barra}
												color={c.quaseCheia ? "secondary" : "primary"}
											/>
										</Tooltip>
										{c.quaseCheia && (
											<div className={classes.erroConta}>
												Conta quase cheia ({c.salvos} de {c.limite}). Conecte outra conta para as próximas conexões.
											</div>
										)}
									</div>
								))}
							</Secao>
						)}

						{/* 3. Conexoes */}
						{contasAtivas.length > 0 && (
							<Secao
								classes={classes}
								icone={<SettingsInputComponent />}
								titulo="3. Qual conta cada conexão usa"
								sub="Sem escolha, os contatos vão para a conta padrão."
							>
								{(dados.conexoes || []).map(w => (
									<div key={w.id} className={classes.conexao}>
										<span className={classes.nomeConexao}>{w.name}</span>
										<Select
											className={classes.select}
											variant="outlined"
											margin="dense"
											value={w.googleContaId || ""}
											displayEmpty
											onChange={e => acao(() => api.put(`/google-contatos/conexao/${w.id}`, { contaId: e.target.value || null }), "Conexão atualizada")}
										>
											<MenuItem value="">
												<em>{padrao ? `Padrão (${padrao.email})` : "Nenhuma (sem conta padrão)"}</em>
											</MenuItem>
											{contasAtivas.map(c => (
												<MenuItem key={c.id} value={c.id}>{c.email}</MenuItem>
											))}
										</Select>
										<Button size="small" className={classes.secundario} onClick={() => conectarPeloCelular(w.id, false, w.name)}>
											QR Code só desta conexão
										</Button>
									</div>
								))}
							</Secao>
						)}

						{/* 4. Antigos */}
						{contasAtivas.length > 0 && (
							<Secao classes={classes} icone={<History />} titulo="4. Clientes antigos" sub="Os novos entram sozinhos; aqui você coloca na fila quem já falou com vocês.">
								<div className={classes.linha}>
									<span className={classes.texto}>Quem falou nos últimos</span>
									<TextField
										className={classes.campo}
										style={{ width: 90, marginBottom: 0 }}
										variant="outlined"
										size="small"
										type="number"
										value={dias}
										onChange={e => setDias(e.target.value)}
										inputProps={{ min: 1, max: 365 }}
									/>
									<span className={classes.texto}>dias</span>
									<Button
										className={classes.primario}
										onClick={() => acao(() => api.post("/google-contatos/importar", { dias: Number(dias) || 30 }), d => `${d?.naFila || 0} clientes conferidos e colocados na fila`)}
									>
										Colocar na fila
									</Button>
								</div>
							</Secao>
						)}

						{dados.ultimosErros?.length > 0 && (
							<Secao classes={classes} icone={<ErrorOutline />} titulo="Últimos erros">
								{dados.ultimosErros.map(e => (
									<div key={e.id} className={classes.erroLinha}>
										<b>{e.nome}</b> (+{e.numero}): {e.erro}
									</div>
								))}
							</Secao>
						)}
					</>
				)}
			</DialogContent>

			<ConfirmarAcaoModal
				open={!!desconectando}
				onClose={() => setDesconectando(null)}
				onConfirm={() => {
					const c = desconectando;
					setDesconectando(null);
					acao(() => api.delete(`/google-contatos/contas/${c.id}`), "Conta desconectada");
				}}
				perigo
				icone={<ContatosIcon />}
				etiqueta="Google Contatos"
				titulo="Desconectar esta conta?"
				subtitulo={desconectando?.email}
				textoConfirmar="Desconectar"
				itens={[
					{ icone: "📇", titulo: "Os contatos já salvos continuam no Google", texto: "Nada é apagado da agenda do celular." },
					{ icone: "⏸️", titulo: "Os próximos clientes deixam de ser salvos nesta conta", texto: "As conexões dela passam a usar a conta padrão (se houver)." }
				]}
			/>

			<Dialog open={!!qr} onClose={() => setQr(null)} maxWidth="xs" fullWidth>
				<DialogContent className={classes.corpo} style={{ alignItems: "center", textAlign: "center" }}>
					<div className={classes.secaoTitulo}>Conectar pelo celular</div>
					<div className={classes.texto}>
						Para <b>{qr?.alvo}</b>. Abra a <b>câmera do celular</b> que tem a conta Google e aponte para o código:
					</div>
					{qr?.url && (
						<div style={{ background: "#fff", padding: 14, borderRadius: 16, display: "inline-block" }}>
							<QRCode value={qr.url} size={230} />
						</div>
					)}
					<div className={classes.texto} style={{ textAlign: "left" }}>
						1. Toque no link que a câmera mostrar.
						<br />
						2. Escolha a conta Google do celular. Se aparecer “O Google não verificou este app”, toque em{" "}
						<b>Avançado → Acessar Consig Agenda</b>.
						<br />
						3. Marque tudo e toque em <b>Continuar</b>. O celular mostra “Conta conectada!” e esta janela fecha sozinha.
					</div>
					<div className={classes.texto} style={{ fontSize: "0.76rem" }}>
						Sem câmera? Copie o link e mande para o celular (ex.: pelo WhatsApp). Vale por 30 minutos.
					</div>
					<div className={classes.linha} style={{ justifyContent: "center" }}>
						<Button className={classes.secundario} onClick={() => copiar(qr?.url || "")}>
							Copiar link
						</Button>
						<Button className={classes.secundario} onClick={() => setQr(null)}>
							Fechar
						</Button>
					</div>
				</DialogContent>
			</Dialog>

			<Dialog open={colarAberto} onClose={() => setColarAberto(false)} maxWidth="sm" fullWidth>
				<DialogContent className={classes.corpo}>
					<div className={classes.secaoTitulo}>Concluir conexão com o Google</div>
					<div className={classes.texto}>
						1. Na aba que abriu, entre com a conta Google do celular e clique em <b>Permitir</b>.
						<br />
						2. A aba vai mostrar “não foi possível conectar”. É normal: copie o endereço inteiro da barra do navegador e cole aqui.
					</div>
					<TextField
						className={classes.campo}
						autoFocus
						fullWidth
						multiline
						rows={3}
						variant="outlined"
						placeholder="http://localhost/?state=...&code=..."
						value={link}
						onChange={e => setLink(e.target.value)}
					/>
					<div className={classes.linha}>
						<Button className={classes.primario} disabled={!link || salvando} onClick={concluirColando}>Concluir</Button>
						<Button className={classes.secundario} onClick={() => setColarAberto(false)}>Cancelar</Button>
					</div>
				</DialogContent>
			</Dialog>
		</Dialog>
	);
};

export default GoogleContatosModal;
